import { Suspense, lazy, useEffect, useState, useCallback, type ReactNode } from 'react';
import { OSProvider, useOS } from '@/lib/os-context';
import { WindowFrame } from '@/components/os/window-frame';
import { Taskbar } from '@/components/os/taskbar';
import { MobileNavBar } from '@/components/os/mobile-nav-bar';
import { DesktopIcon } from '@/components/os/desktop-icon';
import { BootScreen } from '@/components/os/boot-screen';
import { LoginScreen } from '@/components/os/login-screen';
import { NotificationProvider, useNotifications } from '@/components/os/notifications';
import { AchievementProvider, useAchievements, AchievementsApp } from '@/lib/achievements';
import { DesktopWidgets } from '@/components/os/desktop-widgets';
import { SpotlightSearch, useSpotlightShortcut } from '@/components/os/spotlight-search';
import { SnakeGame } from '@/components/os/snake-game';
import { useKonamiCode } from '@/hooks/use-konami-code';
import { useIsMobile } from '@/hooks/use-mobile';
import { ArrowLeft, Trash2, Terminal, StickyNote, FolderGit2, Monitor, User, Globe, Music, Mail, Sparkles, RefreshCw, Search, Gamepad2, BarChart3, Trophy, Timer } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { cn } from '@/lib/utils';
import { RecruiterPortfolio } from '@/components/portfolio/recruiter-portfolio';

type BootPhase = 'booting' | 'login' | 'desktop';

const NotesApp = lazy(() => import('@/components/apps/notes').then((module) => ({ default: module.NotesApp })));
const TrashApp = lazy(() => import('@/components/apps/trash').then((module) => ({ default: module.TrashApp })));
const DisplaySettingsApp = lazy(() => import('@/components/apps/display-settings').then((module) => ({ default: module.DisplaySettingsApp })));
const ProfileApp = lazy(() => import('@/components/apps/profile').then((module) => ({ default: module.ProfileApp })));
const TerminalApp = lazy(() => import('@/components/apps/terminal').then((module) => ({ default: module.TerminalApp })));
const ProjectsApp = lazy(() => import('@/components/apps/projects').then((module) => ({ default: module.ProjectsApp })));
const BrowserApp = lazy(() => import('@/components/apps/browser').then((module) => ({ default: module.BrowserApp })));
const MusicApp = lazy(() => import('@/components/apps/music').then((module) => ({ default: module.MusicApp })));
const FocusStudioApp = lazy(() => import('@/components/apps/focus-studio').then((module) => ({ default: module.FocusStudioApp })));
const ContactApp = lazy(() => import('@/components/apps/contact').then((module) => ({ default: module.ContactApp })));
const GamesApp = lazy(() => import('@/components/apps/games').then((module) => ({ default: module.GamesApp })));
const AnalyticsApp = lazy(() => import('@/components/apps/analytics').then((module) => ({ default: module.AnalyticsApp })));

function AppLoading({ title }: { title: string }) {
  return (
    <div className="flex h-full items-center justify-center bg-background text-muted-foreground">
      <div className="rounded-xl border border-border bg-card px-4 py-3 text-sm shadow-sm">
        Opening {title}...
      </div>
    </div>
  );
}

function lazyWindow(component: ReactNode, title: string) {
  return (
    <Suspense fallback={<AppLoading title={title} />}>
      {component}
    </Suspense>
  );
}

function DesktopContent() {
  const { openWindow, registerWindow, currentWallpaper, effectiveColorMode } = useOS();
  const { addNotification } = useNotifications();
  const { unlockAchievement, updateProgress } = useAchievements();
  const isMobile = useIsMobile();
  const [showWidgets, setShowWidgets] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showSnakeGame, setShowSnakeGame] = useState(false);
  const [openedApps, setOpenedApps] = useState<Set<string>>(new Set());

  // Spotlight search keyboard shortcut
  const openSearch = useCallback(() => setShowSearch(true), []);
  useSpotlightShortcut(openSearch);

  // Konami code easter egg
  useKonamiCode(() => {
    setShowSnakeGame(true);
    unlockAchievement('konami-code');
    addNotification({
      type: 'success',
      title: '🎮 Konami Code Activated!',
      message: 'You found the secret Snake game!',
      duration: 5000,
    });
  });

  // Show welcome notifications on first visit only
  useEffect(() => {
    // Check if this is the first visit
    const hasVisited = localStorage.getItem('hasVisitedPortfolio');
    
    if (!hasVisited) {
      // Mark as visited
      localStorage.setItem('hasVisitedPortfolio', 'true');
      
      // Unlock first visit achievement
      unlockAchievement('first-visit');

      const timer1 = setTimeout(() => {
        addNotification({
          type: 'success',
          title: 'Welcome!',
          message: 'Thanks for visiting my portfolio. Feel free to explore!',
          duration: 6000,
        });
      }, 1500);

      const timer2 = setTimeout(() => {
        addNotification({
          type: 'info',
          title: 'Check out my projects',
          message: 'Click the Projects icon to see my work.',
          duration: 5000,
        });
      }, 4000);

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    }
  }, [addNotification, unlockAchievement]);

  // Register apps on mount
  useEffect(() => {
    registerWindow({
      id: 'notes',
      title: 'My Notes - Lukman Afandi',
      component: lazyWindow(<NotesApp />, 'My Notes'),
      isOpen: false,
      isMinimized: false,
      zIndex: 1,
      icon: <StickyNote className="text-yellow-400" />,
      position: { x: 80, y: 40 },
      size: { width: 1100, height: 750 }
    });

    registerWindow({
      id: 'trash',
      title: 'Trash',
      component: lazyWindow(<TrashApp />, 'Trash'),
      isOpen: false,
      isMinimized: false,
      zIndex: 1,
      icon: <Trash2 className="text-gray-400" />,
      position: { x: 200, y: 100 },
      size: { width: 600, height: 400 }
    });

    registerWindow({
      id: 'display',
      title: 'Display options',
      component: lazyWindow(<DisplaySettingsApp />, 'Display options'),
      isOpen: false,
      isMinimized: false,
      zIndex: 1,
      icon: <Monitor className="text-gray-400" />,
      position: { x: 150, y: 150 },
      size: { width: 700, height: 650 }
    });

    registerWindow({
      id: 'profile',
      title: 'Profile - Lukman Afandi',
      component: lazyWindow(<ProfileApp />, 'Profile'),
      isOpen: false, // Open by default
      isMinimized: false,
      zIndex: 1,
      icon: <User className="text-blue-400" />,
      position: { x: 100, y: 80 },
      size: { width: 900, height: 700 }
    });

    registerWindow({
      id: 'terminal',
      title: 'Terminal',
      component: lazyWindow(<TerminalApp />, 'Terminal'),
      isOpen: false,
      isMinimized: false,
      zIndex: 1,
      icon: <Terminal className="text-green-400" />,
      position: { x: 120, y: 60 },
      size: { width: 800, height: 500 }
    });

    registerWindow({
      id: 'projects',
      title: 'Projects',
      component: lazyWindow(<ProjectsApp />, 'Projects'),
      isOpen: false,
      isMinimized: false,
      zIndex: 1,
      icon: <FolderGit2 className="text-blue-400" />,
      position: { x: 140, y: 80 },
      size: { width: 1000, height: 700 }
    });

    registerWindow({
      id: 'browser',
      title: 'Browser - Social Links',
      component: lazyWindow(<BrowserApp />, 'Browser'),
      isOpen: false,
      isMinimized: false,
      zIndex: 1,
      icon: <Globe className="text-blue-400" />,
      position: { x: 160, y: 100 },
      size: { width: 900, height: 600 }
    });

    registerWindow({
      id: 'music',
      title: 'Music Player',
      component: lazyWindow(<MusicApp />, 'Music Player'),
      isOpen: false,
      isMinimized: false,
      zIndex: 1,
      icon: <Music className="text-green-400" />,
      position: { x: 180, y: 120 },
      size: { width: 420, height: 650 }
    });

    registerWindow({
      id: 'focus',
      title: 'Focus Studio',
      component: lazyWindow(<FocusStudioApp />, 'Focus Studio'),
      isOpen: false,
      isMinimized: false,
      zIndex: 1,
      icon: <Timer className="text-blue-400" />,
      position: { x: 170, y: 90 },
      size: { width: 900, height: 680 }
    });

    registerWindow({
      id: 'contact',
      title: 'Contact Me',
      component: lazyWindow(<ContactApp />, 'Contact'),
      isOpen: false,
      isMinimized: false,
      zIndex: 1,
      icon: <Mail className="text-orange-400" />,
      position: { x: 200, y: 140 },
      size: { width: 890, height: 600 }
    });

    registerWindow({
      id: 'games',
      title: 'Mini Games',
      component: lazyWindow(<GamesApp />, 'Mini Games'),
      isOpen: false,
      isMinimized: false,
      zIndex: 1,
      icon: <Gamepad2 className="text-purple-400" />,
      position: { x: 220, y: 160 },
      size: { width: 900, height: 700 }
    });

    registerWindow({
      id: 'analytics',
      title: 'Analytics Dashboard',
      component: lazyWindow(<AnalyticsApp />, 'Analytics'),
      isOpen: false,
      isMinimized: false,
      zIndex: 1,
      icon: <BarChart3 className="text-primary" />,
      position: { x: 240, y: 180 },
      size: { width: 1100, height: 800 }
    });

    registerWindow({
      id: 'achievements',
      title: 'Achievements',
      component: <AchievementsApp />,
      isOpen: false,
      isMinimized: false,
      zIndex: 1,
      icon: <Trophy className="text-yellow-400" />,
      position: { x: 260, y: 200 },
      size: { width: 900, height: 700 }
    });
  }, [registerWindow]);

  return (
    <ContextMenu>
      <ContextMenuTrigger 
        className={cn(
          "w-screen h-screen overflow-hidden relative selection:bg-blue-500/30 block transition-colors duration-300",
          effectiveColorMode === 'light' ? 'bg-[#f5f5f7]' : 'bg-[#1d1f27]'
        )}
      >
        {/* Wallpaper / Background */}
        {currentWallpaper?.url && (
          <div 
            className="absolute inset-0 pointer-events-none bg-cover bg-center transition-all duration-500"
            style={{ 
              backgroundImage: `url('${currentWallpaper.url}')`
            }}
          />
        )}

        {/* Desktop Icons Grid - Vertical on desktop, grid on mobile */}
        <div className={cn(
          "z-0",
          isMobile 
            ? "fixed bottom-16 left-0 right-0 flex flex-wrap justify-center gap-2 px-3 pb-3 pt-2 bg-gradient-to-t from-black/40 to-transparent" 
            : "absolute top-4 bottom-24 left-4 right-4 flex flex-col flex-wrap content-start items-start gap-x-3 gap-y-4 overflow-hidden pr-2"
        )}>
          <DesktopIcon 
            label="Profile" 
            icon={<User className="w-6 h-6 text-blue-400" />} 
            onClick={() => openWindow('profile')} 
          />
          <DesktopIcon 
            label="My Notes" 
            icon={<StickyNote className="w-6 h-6 text-yellow-400" />} 
            onClick={() => openWindow('notes')} 
          />
          <DesktopIcon 
            label="Projects" 
            icon={<FolderGit2 className="w-6 h-6 text-blue-400" />} 
            onClick={() => openWindow('projects')} 
          />
          <DesktopIcon 
            label="Terminal" 
            icon={<Terminal className="w-6 h-6 text-green-400" />} 
            onClick={() => openWindow('terminal')} 
          />
          <DesktopIcon 
            label="Browser" 
            icon={<Globe className="w-6 h-6 text-blue-400" />} 
            onClick={() => openWindow('browser')} 
          />
          {/* <DesktopIcon 
            label="Music" 
            icon={<Music className="w-6 h-6 text-green-400" />} 
            onClick={() => openWindow('music')} 
          /> */}
          <DesktopIcon
            label="Focus"
            icon={<Timer className="w-6 h-6 text-blue-400" />}
            onClick={() => openWindow('focus')}
          />
          <DesktopIcon 
            label="Contact" 
            icon={<Mail className="w-6 h-6 text-orange-400" />} 
            onClick={() => openWindow('contact')} 
          />
          <DesktopIcon 
            label="Mini Games" 
            icon={<Gamepad2 className="w-6 h-6 text-purple-400" />} 
            onClick={() => openWindow('games')} 
          />
          <DesktopIcon 
            label="Analytics" 
            icon={<BarChart3 className="w-6 h-6 text-primary" />} 
            onClick={() => openWindow('analytics')} 
          />
          <DesktopIcon 
            label="Achievements" 
            icon={<Trophy className="w-6 h-6 text-yellow-400" />} 
            onClick={() => openWindow('achievements')} 
          />
          <DesktopIcon 
            label="Trash" 
            icon={<Trash2 className="w-6 h-6 text-gray-400" />} 
            onClick={() => openWindow('trash')} 
          />
        </div>

        {/* Windows Layer */}
        <WindowFrame id="profile" title="Profile - Lukman Afandi" initialSize={{ width: 900, height: 700 }}>
          {lazyWindow(<ProfileApp />, 'Profile')}
        </WindowFrame>

        <WindowFrame id="notes" title="My Notes - Lukman Afandi">
          {lazyWindow(<NotesApp />, 'My Notes')}
        </WindowFrame>
        
        <WindowFrame id="trash" title="Trash" initialSize={{ width: 600, height: 400 }}>
          {lazyWindow(<TrashApp />, 'Trash')}
        </WindowFrame>

        <WindowFrame id="display" title="Display options" initialSize={{ width: 700, height: 650 }}>
          {lazyWindow(<DisplaySettingsApp />, 'Display options')}
        </WindowFrame>

        <WindowFrame id="terminal" title="Terminal" initialSize={{ width: 800, height: 500 }}>
          {lazyWindow(<TerminalApp />, 'Terminal')}
        </WindowFrame>

        <WindowFrame id="projects" title="Projects" initialSize={{ width: 1000, height: 700 }}>
          {lazyWindow(<ProjectsApp />, 'Projects')}
        </WindowFrame>

        <WindowFrame id="browser" title="Browser - Social Links" initialSize={{ width: 900, height: 600 }}>
          {lazyWindow(<BrowserApp />, 'Browser')}
        </WindowFrame>

        <WindowFrame id="music" title="Music Player" initialSize={{ width: 420, height: 650 }}>
          {lazyWindow(<MusicApp />, 'Music Player')}
        </WindowFrame>

        <WindowFrame id="focus" title="Focus Studio" initialSize={{ width: 900, height: 680 }}>
          {lazyWindow(<FocusStudioApp />, 'Focus Studio')}
        </WindowFrame>

        <WindowFrame id="contact" title="Contact Me" initialSize={{ width: 890, height: 600 }}>
          {lazyWindow(<ContactApp />, 'Contact')}
        </WindowFrame>

        <WindowFrame id="games" title="Mini Games" initialSize={{ width: 900, height: 700 }}>
          {lazyWindow(<GamesApp />, 'Mini Games')}
        </WindowFrame>

        <WindowFrame id="analytics" title="Analytics Dashboard" initialSize={{ width: 1100, height: 800 }}>
          {lazyWindow(<AnalyticsApp />, 'Analytics')}
        </WindowFrame>

        <WindowFrame id="achievements" title="Achievements" initialSize={{ width: 900, height: 700 }}>
          <AchievementsApp />
        </WindowFrame>

        {/* Desktop Widgets - Hidden on mobile */}
        <AnimatePresence>
          {showWidgets && !isMobile && <DesktopWidgets enabled={showWidgets} />}
        </AnimatePresence>

        {/* Spotlight Search */}
        <SpotlightSearch isOpen={showSearch} onClose={() => setShowSearch(false)} />

        {/* Secret Snake Game (Konami Code) */}
        <AnimatePresence>
          {showSnakeGame && <SnakeGame onClose={() => setShowSnakeGame(false)} />}
        </AnimatePresence>

        {/* Navigation Bar - Different for mobile and desktop */}
        {isMobile ? (
          <MobileNavBar 
            onSearchClick={() => setShowSearch(true)} 
            onHomeClick={() => {
              // Close all windows or show home screen
            }}
          />
        ) : (
          <Taskbar onSearchClick={() => setShowSearch(true)} />
        )}
      </ContextMenuTrigger>
      <ContextMenuContent className="w-64 bg-popover border-border text-popover-foreground">
        <ContextMenuItem inset onClick={() => setShowSearch(true)} className="focus:bg-accent focus:text-accent-foreground cursor-pointer">
          <Search className="mr-2 h-4 w-4" />
          Search (⌘K)
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem inset onClick={() => openWindow('display')} className="focus:bg-accent focus:text-accent-foreground cursor-pointer">
          <Monitor className="mr-2 h-4 w-4" />
          Display Settings
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem inset onClick={() => setShowWidgets(!showWidgets)} className="focus:bg-accent focus:text-accent-foreground cursor-pointer">
          <Sparkles className="mr-2 h-4 w-4" />
          {showWidgets ? 'Hide Widgets' : 'Show Widgets'}
        </ContextMenuItem>
        <ContextMenuItem inset onClick={() => window.location.reload()} className="focus:bg-accent focus:text-accent-foreground cursor-pointer">
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh Desktop
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}

function Desktop({ onExitDesktop }: { onExitDesktop: () => void }) {
  // ============================================
  // 🔧 TOGGLE BOOT/LOGIN SCREENS HERE:
  // ============================================
  // Set to 'desktop' to skip boot and login screens
  // Set to 'booting' to show boot screen first
  // Set to 'login' to skip boot but show login
  const INITIAL_PHASE: BootPhase = 'desktop'; // Change this to 'booting' to re-enable
  // ============================================
  
  const [bootPhase, setBootPhase] = useState<BootPhase>(INITIAL_PHASE);

  return (
    <NotificationProvider>
      <AnimatePresence mode="wait">
        {bootPhase === 'booting' && (
          <BootScreen 
            key="boot"
            onComplete={() => setBootPhase('login')} 
          />
        )}
        
        {bootPhase === 'login' && (
          <LoginScreen 
            key="login"
            onUnlock={() => setBootPhase('desktop')} 
          />
        )}

        {bootPhase === 'desktop' && (
          <motion.div
            key="desktop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <button
              type="button"
              onClick={onExitDesktop}
              className="fixed right-4 top-4 z-[10001] flex min-h-11 items-center gap-2 rounded-full border border-white/70 bg-white/90 px-4 text-sm font-semibold text-slate-900 shadow-lg backdrop-blur-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
              aria-label="Return to portfolio overview"
            >
              <ArrowLeft className="h-4 w-4" /> Portfolio overview
            </button>
            <DesktopContent />
          </motion.div>
        )}
      </AnimatePresence>
    </NotificationProvider>
  );
}

export default function Home() {
  const [experience, setExperience] = useState<'overview' | 'desktop'>('overview');

  const enterDesktop = () => {
    localStorage.removeItem('windowStates');
    setExperience('desktop');
  };

  if (experience === 'overview') {
    return <RecruiterPortfolio onEnterDesktop={enterDesktop} />;
  }

  return (
    <OSProvider key="desktop-experience">
      <AchievementProvider>
        <Desktop onExitDesktop={() => setExperience('overview')} />
      </AchievementProvider>
    </OSProvider>
  );
}
